// ============================================================================
// PortalAprovacoes.jsx — B3 (Client Side)
// Exibe peças enviadas pelo advogado para aprovação do cliente.
// Coleção: artifacts/{appId}/public/data/portal_piece_approvals
// ============================================================================

import React, { useEffect, useState } from 'react';
import { CheckCircle, Clock, ExternalLink, RefreshCw, XCircle } from 'lucide-react';
import { FIREBASE_CONFIG } from '../../config/Config';
import { subscribePortalPieceApprovals, approvePortalPiece, requestRevisionPortalPiece } from '../../services/PortalAprovacoesRepository';

const appId = FIREBASE_CONFIG.projectId;

const STATUS_LABEL = {
  pending:            'Aguardando sua aprovação',
  approved:           'Aprovado por você',
  revision_requested: 'Revisão solicitada',
};

const STATUS_COLOR = {
  pending:            'bg-amber-50 border-amber-200 text-amber-700',
  approved:           'bg-emerald-50 border-emerald-200 text-emerald-700',
  revision_requested: 'bg-blue-50 border-blue-200 text-blue-700',
};

const STATUS_ICON = {
  pending:            Clock,
  approved:           CheckCircle,
  revision_requested: RefreshCw,
};

const fmtDate = (ts) => {
  try {
    const d = typeof ts?.toDate === 'function' ? ts.toDate() : new Date(ts);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return ''; }
};

export default function PortalAprovacoes({ clientId }) {
  const [items, setItems]               = useState([]);
  const [loading, setLoading]           = useState(true);
  const [expandedId, setExpandedId]     = useState(null);
  const [revisionNote, setRevisionNote] = useState('');
  const [working, setWorking]           = useState(null);
  const [notice, setNotice]             = useState('');

  useEffect(() => {
    if (!clientId || !appId) { setLoading(false); return; }
    const unsub = subscribePortalPieceApprovals(clientId, (items) => {
      setItems(items);
      setLoading(false);
    }, appId);
    return () => unsub();
  }, [clientId]);

  const flash = (msg) => { setNotice(msg); setTimeout(() => setNotice(''), 3000); };

  const handleApprove = async (item) => {
    if (working) return;
    setWorking(item.id);
    try {
      await approvePortalPiece(item.id, appId);
      flash('Peça aprovada com sucesso!');
    } catch { flash('Erro ao aprovar. Tente novamente.'); }
    setWorking(null);
    setExpandedId(null);
  };

  const handleRequestRevision = async (item) => {
    if (working) return;
    const note = revisionNote.trim();
    setWorking(item.id);
    try {
      await requestRevisionPortalPiece(item.id, note, appId);
      flash('Solicitação de revisão enviada ao escritório.');
    } catch { flash('Erro ao enviar. Tente novamente.'); }
    setWorking(null);
    setRevisionNote('');
    setExpandedId(null);
  };

  const pendingCount = items.filter(i => i.status === 'pending').length;

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-3">
        <div className="h-3 w-3 animate-spin rounded-full border-2 border-gray-300 border-t-[#C5A059]" />
        <span className="text-xs text-gray-500">Carregando aprovações...</span>
      </div>
    );
  }

  if (items.length === 0) return null;

  return (
    <div className="border border-gray-300 rounded-lg bg-white overflow-hidden">
      <div className="px-3 py-2 border-b border-gray-200 bg-gray-50 flex items-center gap-2">
        <span className="text-xs font-bold text-[#20243B]">Peças para Aprovação</span>
        {pendingCount > 0 && (
          <span className="h-5 min-w-5 px-1.5 rounded-full bg-amber-500 text-white text-[10px] font-bold flex items-center justify-center">
            {pendingCount}
          </span>
        )}
      </div>
      <div className="px-3 py-3 space-y-3">

      {notice && (
        <div className="text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
          {notice}
        </div>
      )}

      <div className="space-y-2">
        {items.map((item) => {
          const StatusIcon = STATUS_ICON[item.status] || Clock;
          const isExpanded = expandedId === item.id;
          const isPending  = item.status === 'pending';

          return (
            <div key={item.id} className="border border-gray-200 rounded-xl overflow-hidden bg-white shadow-sm">
              <div className="px-4 py-3 flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-bold text-[#20243B] truncate">
                      {String(item.pieceType || 'Documento').trim()}
                    </span>
                    {item.workspaceName && (
                      <span className="text-[10px] text-gray-500 truncate">· {item.workspaceName}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full border ${STATUS_COLOR[item.status] || STATUS_COLOR.pending}`}>
                      <StatusIcon className="w-3 h-3" />
                      {STATUS_LABEL[item.status] || item.status}
                    </span>
                    {item.createdAt && (
                      <span className="text-[10px] text-gray-400">{fmtDate(item.createdAt)}</span>
                    )}
                  </div>

                  {item.lawyerNote && (
                    <p className="mt-1.5 text-xs text-gray-600 italic leading-relaxed">
                      "{item.lawyerNote}"
                    </p>
                  )}

                  {item.status === 'revision_requested' && item.clientNote && (
                    <p className="mt-1.5 text-xs text-blue-700 bg-blue-50 border border-blue-100 rounded-lg px-2 py-1">
                      <span className="font-semibold">Seu comentário:</span> {item.clientNote}
                    </p>
                  )}
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  {item.driveUrl && (
                    <a
                      href={item.driveUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 text-[10px] font-semibold text-[#20243B] border border-gray-200 rounded-lg px-2 py-1 hover:bg-gray-50"
                    >
                      <ExternalLink className="w-3 h-3" /> Ver
                    </a>
                  )}
                  {isPending && (
                    <button
                      type="button"
                      onClick={() => setExpandedId(isExpanded ? null : item.id)}
                      className="text-[10px] font-bold text-[#C5A059] border border-[#C5A059]/30 rounded-lg px-2 py-1 hover:bg-[#C5A059]/10"
                    >
                      {isExpanded ? 'Fechar' : 'Responder'}
                    </button>
                  )}
                </div>
              </div>

              {isExpanded && isPending && (
                <div className="border-t border-gray-100 bg-gray-50/60 px-4 py-3 space-y-3">
                  <div className="flex gap-2">
                    <button
                      type="button"
                      disabled={!!working}
                      onClick={() => handleApprove(item)}
                      className="flex-1 flex items-center justify-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold py-2 rounded-lg disabled:opacity-50"
                    >
                      {working === item.id ? (
                        <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                      ) : (
                        <CheckCircle className="w-3.5 h-3.5" />
                      )}
                      Aprovar
                    </button>
                    <button
                      type="button"
                      onClick={() => setExpandedId(expandedId === item.id + '_rev' ? item.id : item.id + '_rev')}
                      className="flex-1 flex items-center justify-center gap-1.5 bg-white border border-blue-300 text-blue-700 text-xs font-bold py-2 rounded-lg hover:bg-blue-50"
                    >
                      <RefreshCw className="w-3.5 h-3.5" />
                      Pedir Revisão
                    </button>
                  </div>

                  {expandedId === item.id + '_rev' && (
                    <div className="space-y-2">
                      <textarea
                        value={revisionNote}
                        onChange={e => setRevisionNote(e.target.value)}
                        placeholder="Descreva o que deve ser revisado..."
                        rows={3}
                        className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 resize-none outline-none focus:border-[#20243B] placeholder-gray-400"
                      />
                      <button
                        type="button"
                        disabled={!revisionNote.trim() || !!working}
                        onClick={() => handleRequestRevision(item)}
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold py-2 rounded-lg disabled:opacity-50"
                      >
                        {working === item.id ? 'Enviando...' : 'Enviar Solicitação'}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
      </div>
    </div>
  );
}
